import { EventType } from "@/analytics/analytics-events";
import type { ApplyManualBlursProperties } from "@/analytics/manual-blurring-events-properties";
import {
  useFileUploaderWithPromise,
  type UploadedFile,
} from "@/components/common/file-upload-context/use-file-uploader";
import { UploadElementType } from "@/components/common/point-cloud-file-upload-context/use-upload-element-types";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useReload3DObjectInCache } from "@/object-cache";
import { selectBackgroundTasks } from "@/store/background-tasks/background-tasks-selector";
import {
  addBackgroundTask,
  updateBackgroundTask,
} from "@/store/background-tasks/background-tasks-slice";
import { store } from "@/store/store";
import { useAppDispatch } from "@/store/store-hooks";
import { deactivateTool } from "@/store/ui/ui-slice";
import type { ManualBlurringTask } from "@/utils/background-tasks";
import {
  purgeImg360FromLotVCache,
  reloadImg360InBrowserCache,
} from "@/utils/cache-utils";
import { selectPanoAnnotationsAdjustedPose } from "@/utils/camera-transform";
import { createOffscreenCanvasForImageData } from "@/utils/canvas-utils";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import {
  Alert,
  FaroText,
  resizeImage,
  useDialog,
  useToast,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, generateGUID, type GUID } from "@faro-lotv/foundation";
import {
  isIElementImg360,
  type IElementImg360,
} from "@faro-lotv/ielement-types";
import { applyBlursToPano } from "@faro-lotv/lotv";
import {
  createSignedUrlForIElementUri,
  selectIElement,
} from "@faro-lotv/project-source";
import {
  BackgroundTaskState,
  convertLevelOfDetailToSlicePackDimension,
  createMutationManualFaceBlur,
  useApiClientContext,
  type CoreApiClient,
  type ProjectApi,
} from "@faro-lotv/service-wires";
import { Stack } from "@mui/system";
import { useCallback } from "react";
import { Matrix4 } from "three";
import { MANUAL_BLUR_COLOR } from "./manual-blurring-constants";
import {
  useManualBlurringStore,
  useResetManualBlurringStore,
} from "./manual-blurring-store";
import type { ManualBlur } from "./manual-blurring-types";

/** The task progress values to show to the user, to roughly indicate the progress of the task. */
const TASK_PROGRESS_STEPS = {
  started: 10,
  afterBlurring: 50,
  afterUpload: 75,
  afterSlicing: 90,
};

/**
 * The callback to apply the manual blurs in the store
 *
 * @param hasConfirmed Whether the user has already confirmed the action, so no more dialogs are shown.
 * @param analyticsVia Analytics property used to understand where the apply action was triggered from.
 */
type ApplyManualBlurringCallback = (
  hasConfirmed?: boolean,
  analyticsVia?: ApplyManualBlursProperties["via"],
) => Promise<void>;

/** @returns A callback that applies the pending changes in the blurring store when called. */
export function useApplyManualBlurring(): ApplyManualBlurringCallback {
  const dispatch = useAppDispatch();

  const resetManualBlurringStore = useResetManualBlurringStore();
  const { startSavingManualBlurs, finishSavingManualBlurs } =
    useManualBlurringStore();

  const { projectApiClient, coreApiClient } = useApiClientContext();
  const uploadFile = useFileUploaderWithPromise();

  const reload3DObject = useReload3DObjectInCache();

  const { handleErrorWithToast } = useErrorHandlers();
  const { createDialog } = useDialog();
  const { openToast } = useToast();

  return useCallback(
    async (
      hasConfirmedProp: boolean = false,
      analyticsVia: ApplyManualBlursProperties["via"] = "toolbar",
    ) => {
      const elementsToBlur = Object.entries(
        useManualBlurringStore.getState().manualBlurs,
      );

      const hasConfirmed =
        hasConfirmedProp ||
        (await createDialog({
          title: "Apply manual blurs",
          content: (
            <Stack sx={{ gap: 2 }}>
              <FaroText variant="bodyM">
                Your manual blurs will be permanently applied to the 360 photo.
                Do you want to proceed?
              </FaroText>

              <Alert variant="warning" title="The operation cannot be undone" />
            </Stack>
          ),
        }));

      if (!hasConfirmed) return;

      const numBlurs = elementsToBlur.reduce(
        (sum, [, blurs]) => sum + (blurs?.length ?? 0),
        0,
      );
      const numPanos = elementsToBlur.filter(
        ([, blurs]) => !!blurs && blurs.length > 0,
      ).length;

      Analytics.track<ApplyManualBlursProperties>(EventType.applyManualBlurs, {
        via: analyticsVia,
        numBlurs,
        numPanos,
      });

      openToast({
        title: "Applying manual blurs",
        message:
          "Your manual blurs are being applied. You can continue working while the blurring is in progress.",
      });

      try {
        const promises = Promise.all(
          elementsToBlur.map(async ([iElementId, blurs]) => {
            if (!blurs || blurs.length === 0) return;

            startSavingManualBlurs(iElementId);

            const task = createManualBlurringTask(iElementId);
            dispatch(addBackgroundTask(task));

            function setTaskProgress(progress: number): void {
              dispatch(
                updateBackgroundTask({
                  ...task,
                  state: BackgroundTaskState.started,
                  progress,
                }),
              );
            }

            try {
              // Avoid blurring race conditions, if there is already another blurring task active for the same pano.
              await waitForNoPendingBlurringTasksOnElement(iElementId);

              setTaskProgress(TASK_PROGRESS_STEPS.started);

              const panoElement = selectIElement(iElementId)(store.getState());
              assert(
                panoElement && isIElementImg360(panoElement),
                "Expected Img360 to blur to be loaded",
              );

              const imageData = await blurPanoImage(
                panoElement,
                blurs,
                projectApiClient,
              );

              setTaskProgress(TASK_PROGRESS_STEPS.afterBlurring);

              const { uploadedFile, uploadedFileSize, uploadedThumbnailFile } =
                await uploadBlurredPanoFiles(
                  imageData,
                  projectApiClient,
                  coreApiClient,
                  uploadFile,
                );

              setTaskProgress(TASK_PROGRESS_STEPS.afterUpload);

              const { levelOfDetails } = await projectApiClient.slicePanoImage({
                originalImgUri: uploadedFile.downloadUrl,
                // The manual blur mutation expects to receive exactly the same slices as in the original pano.
                // Since the algorithm for slicing can vary, we need to make sure to replicate the original slicing levels exactly.
                slicePacks: panoElement.levelsOfDetail?.map(
                  convertLevelOfDetailToSlicePackDimension,
                ),
              });

              setTaskProgress(TASK_PROGRESS_STEPS.afterSlicing);

              await projectApiClient.applyMutations([
                createMutationManualFaceBlur(iElementId, {
                  blurredImageUrl: uploadedFile.downloadUrl,
                  blurredThumbnailUrl: uploadedThumbnailFile.downloadUrl,
                  blurredLevelsOfDetail: levelOfDetails,
                  blurredImageMd5Hash: uploadedFile.md5,
                  blurredImageFileSize: uploadedFileSize,
                }),
              ]);

              // Reload the pano object, after purging all caches
              purgeImg360FromLotVCache(panoElement);
              await reloadImg360InBrowserCache(panoElement);
              await reload3DObject(panoElement);

              dispatch(
                updateBackgroundTask({
                  ...task,
                  state: BackgroundTaskState.succeeded,
                  progress: 100,
                }),
              );
            } catch (error) {
              dispatch(
                updateBackgroundTask({
                  ...task,
                  state: BackgroundTaskState.failed,
                }),
              );
              throw error;
            } finally {
              finishSavingManualBlurs(
                iElementId,
                blurs.map((blur) => blur.id),
              );
            }
          }),
        );

        // Reset the tool, so new blurs can be drawn, and to clear the undo stack.
        resetManualBlurringStore();
        dispatch(deactivateTool());

        await promises;
      } catch (error) {
        handleErrorWithToast({
          title: "Could not apply manual blurs",
          error,
        });
      }
    },
    [
      coreApiClient,
      createDialog,
      dispatch,
      finishSavingManualBlurs,
      handleErrorWithToast,
      openToast,
      projectApiClient,
      reload3DObject,
      resetManualBlurringStore,
      startSavingManualBlurs,
      uploadFile,
    ],
  );
}

async function blurPanoImage(
  panoElement: IElementImg360,
  blurs: ManualBlur[],
  projectApiClient: ProjectApi,
): Promise<ImageData> {
  const signedUrl = createSignedUrlForIElementUri(
    projectApiClient,
    panoElement,
  );

  const panoOffset = new Matrix4().fromArray(
    selectPanoAnnotationsAdjustedPose(panoElement)(store.getState()),
  );
  const panoWorldMatrix = selectIElementWorldMatrix4(panoElement.id)(
    store.getState(),
  )
    .clone()
    .premultiply(panoOffset);

  return applyBlursToPano(
    await signedUrl.requestSignedUrl(),
    panoWorldMatrix,
    blurs,
    MANUAL_BLUR_COLOR,
  );
}

/** The upload results for the blurred image and thumbnail files */
type UploadBlurredPanoFilesResult = {
  /** Upload result of the image file */
  uploadedFile: UploadedFile;

  /** Size of the file that was created for the image data. */
  uploadedFileSize: number;

  /** Upload result of the thumbnail */
  uploadedThumbnailFile: UploadedFile;
};

async function uploadBlurredPanoFiles(
  imageData: ImageData,
  projectApiClient: ProjectApi,
  coreApiClient: CoreApiClient,
  uploadFile: ReturnType<typeof useFileUploaderWithPromise>,
): Promise<UploadBlurredPanoFilesResult> {
  const canvas = createOffscreenCanvasForImageData(imageData);
  const imageBlob = await canvas.convertToBlob({ type: "image/png" });
  const blurredImageFile = new File([imageBlob], "blurred_pano.png", {
    type: "image/png",
  });

  const blurredThumbnailFile = await resizeImage(blurredImageFile);

  const [uploadedFile, uploadedThumbnailFile] = await Promise.all([
    uploadFile({
      file: blurredImageFile,
      uploadElementType: UploadElementType.none,
      projectId: projectApiClient.projectId,
      coreApiClient,
      silent: true,
    }),
    uploadFile({
      file: blurredThumbnailFile,
      uploadElementType: UploadElementType.none,
      projectId: projectApiClient.projectId,
      coreApiClient,
      silent: true,
    }),
  ]);

  return {
    uploadedFile,
    uploadedFileSize: blurredImageFile.size,
    uploadedThumbnailFile,
  };
}

function createManualBlurringTask(iElementId: GUID): ManualBlurringTask {
  const isoDate = new Date().toISOString();
  return {
    type: "ManualBlurring",
    id: generateGUID(),
    iElementId,
    createdAt: isoDate,
    changedAt: isoDate,
    devMessage: "Blurring a panorama",
    state: BackgroundTaskState.scheduled,
    progress: 0,
    shouldPreventWindowClose: true,
  };
}

async function waitForNoPendingBlurringTasksOnElement(
  iElementId: GUID,
): Promise<void> {
  // Continuously check if there are any active manual blurring tasks on the given element.
  while (
    selectBackgroundTasks(
      (task) =>
        task.type === "ManualBlurring" &&
        task.iElementId === iElementId &&
        task.state === BackgroundTaskState.started,
    )(store.getState()).length > 0
  ) {
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }
}
